import { useMemo } from 'react';
import { AtomicItemListType, type IScreensComponent } from '../../api';
import { AblyGoodsCardActionType } from '../../api/types';
import type { FeedbackType } from '../../api/feedbackService/types';
import type { RowItem } from './types';
import { isNotNil } from '../../utils';
import { getThreeColGoodsListData } from './ItemList/ThreeColGoodsList/getThreeColGoodsListData';
import { getTwoColGoodsListData } from './ItemList/TwoColGoodsList/getTwoColGoodsListData';
import { cardRenderItemGenerator } from './ItemList/TwoColCardList/cardRenderItemGenerator';
import type { GoodsCardEntity } from './ItemList/TwoColCardList/types';

/**
 * 수직×수직 List 중첩으로 가상화가 무력화되는 컴포넌트 타입.
 * 이 타입들은 내부에 <List numColumns={N}>을 가지므로,
 * 행 단위로 평탄화하여 단일 FlatList에서 가상화한다.
 */
export const FLATTEN_ITEM_TYPES = new Set([
  AtomicItemListType.ThreeColGoodsList,
  AtomicItemListType.ThreeColCardList,
  AtomicItemListType.TwoColGoodsList,
  AtomicItemListType.TwoColCardList,
]);

const TWO_COL_TYPES = new Set([
  AtomicItemListType.TwoColGoodsList,
  AtomicItemListType.TwoColCardList,
]);

// 평탄화된 리스트 아이템: 상품 행(3col/2col), 분리된 헤더/푸터, 또는 원본 컴포넌트
export type FlattenedItem =
  | { kind: 'row-3col'; key: string; items: RowItem[] }
  | { kind: 'row-2col'; key: string; items: RowItem[] }
  | {
      kind: 'component';
      key: string;
      component: IScreensComponent;
      originalIndex: number;
    }
  | {
      kind: 'proxy';
      key: string;
      component: IScreensComponent;
      originalIndex: number;
    }
  // 평탄화 대상 컴포넌트의 entity.header를 별도 행으로 분리
  | {
      kind: 'header';
      key: string;
      component: IScreensComponent;
      originalIndex: number;
    }
  // 평탄화 대상 컴포넌트의 entity.footer를 별도 행으로 분리
  | {
      kind: 'footer';
      key: string;
      component: IScreensComponent;
      originalIndex: number;
    }
  | { kind: 'skeleton-3col'; key: string }
  | { kind: 'skeleton-2col'; key: string };

/**
 * 서버 컴포넌트 배열을 행 단위로 평탄화하는 훅.
 *
 * 2col/3col GoodsList/CardList는 내부에 수직 List를 가져서
 * 수직×수직 중첩이 발생하므로, 행 단위로 분해하여 단일 FlatList에서 가상화한다.
 * 나머지 컴포넌트는 원본 그대로 유지한다.
 */
/** 행 내 아이템들의 식별자로 key 생성 (필터 변경 시 FlatList 셀 갱신 보장) */
const rowKey = (prefix: string, index: number, chunk: RowItem[]): string => {
  const ids = chunk.map(item =>
    item.type === 'goods'
      ? item.sno
      : item.type === 'primaryCard'
        ? item.key
        : 'ph',
  );
  return `${prefix}-${index}-${ids.join('-')}`;
};

/** 순수 변환 함수 — 테스트 가능 */
export const flattenComponents = (
  effectiveComponents: IScreensComponent[],
  // originalIndices: effectiveComponents → data.components 인덱스 매핑.
  // inject로 들어간 컴포넌트는 -1. 미지정 시 ci 그대로 사용(이전 동작 호환).
  // componentIndex 좌표계가 effectiveComponents(post-inject)와 data.components(pre-inject)
  // 사이에서 어긋나 inject afterIndex가 잘못 매핑되던 버그를 해결하기 위해 도입.
  originalIndices?: number[],
): FlattenedItem[] => {
  const items: FlattenedItem[] = [];
  let rowCounter = 0;

  for (let ci = 0; ci < effectiveComponents.length; ci++) {
    const component = effectiveComponents[ci];
    const itemListType = component.type.itemList;
    // data.components 기준 인덱스 (inject 컴포넌트는 -1)
    const dataIdx = originalIndices?.[ci] ?? ci;
    // inject 컴포넌트 내 행은 onReturn으로 nested inject 트리거 안 되도록 undefined로
    // 흘려보내 click 핸들러의 `item.componentIndex != null` 가드에서 자연 스킵.
    const rowComponentIndex = dataIdx === -1 ? undefined : dataIdx;

    // 평탄화 대상이 아닌 컴포넌트 — 원본 그대로
    if (!itemListType || !FLATTEN_ITEM_TYPES.has(itemListType)) {
      if (component.type.itemList === AtomicItemListType.Proxy) {
        items.push({
          kind: 'proxy',
          key: `${component.entity.body.item.url}`,
          component,
          originalIndex: dataIdx,
        });
      } else {
        items.push({
          kind: 'component',
          key: `comp-${ci}-${component.componentId}`,
          component,
          originalIndex: dataIdx,
        });
      }
      continue;
    }

    // 평탄화 대상의 헤더 — 별도 행으로 분리
    // (평탄화 시 entity.itemList만 row로 분해되어 header/footer가 누락되는 이슈 fix)
    if (component.type.header && component.entity.header) {
      items.push({
        kind: 'header',
        key: `header-${ci}-${component.componentId}`,
        component,
        originalIndex: dataIdx,
      });
    }

    const entityList = component.entity.itemList;

    // === 2col 평탄화 ===
    if (TWO_COL_TYPES.has(itemListType)) {
      let twoColItems: RowItem[] = [];

      if (itemListType === AtomicItemListType.TwoColGoodsList) {
        const goodsData = getTwoColGoodsListData(entityList);
        twoColItems = goodsData.map((item, ei) => ({
          type: 'goods' as const,
          sno: item.sno,
          data: item,
          componentIndex: rowComponentIndex,
          entityIndex: ei,
          logging: item.logging,
          ad: item.item?.ad,
          firstPageRendering: item.item?.first_page_rendering ?? null,
          listId: item.logging?.analytics?.LIST_ID as string | undefined,
          feedbackType:
            (item.item?.feedback_type as FeedbackType | null) ?? null,
          hasMoreAction:
            item.render?.data.actions?.includes(AblyGoodsCardActionType.More) ??
            false,
          onReturn: item.item?.on_return ?? undefined,
        }));
      } else if (itemListType === AtomicItemListType.TwoColCardList) {
        if (!entityList) continue;
        const cardData = cardRenderItemGenerator(entityList);
        const showRanking = entityList
          .filter((e): e is GoodsCardEntity => e.type !== 'PRIMARY_CARD')
          .some(e => isNotNil(e.render?.data.ranking));

        twoColItems = cardData.map((item, idx) => {
          if (item.type === 'PRIMARY_CARD') {
            return {
              type: 'primaryCard' as const,
              item: item.item,
              logging: item.logging,
              key: `primary-${component.componentId}-${idx}`,
            };
          }
          if (
            item.type === 'VIDEO_GOODS_CARD' ||
            item.type === 'FULL_VIDEO_GOODS_CARD'
          ) {
            return {
              type: 'videoGoods' as const,
              cardType: item.type,
              sno: item.item.sno,
              item: item.item,
              logging: item.logging,
              componentIndex: rowComponentIndex,
              entityIndex: idx,
            };
          }
          return {
            type: 'goods' as const,
            sno: item.sno,
            data: item,
            componentIndex: rowComponentIndex,
            entityIndex: idx,
            ranking: showRanking ? idx + 1 : undefined,
            logging: item.logging,
            ad: item.item?.ad,
            firstPageRendering: item.item?.first_page_rendering ?? null,
            listId: item.logging?.analytics?.LIST_ID as string | undefined,
            feedbackType:
              (item.item?.feedback_type as FeedbackType | null) ?? null,
            hasMoreAction:
              item.render?.data.actions?.includes(
                AblyGoodsCardActionType.More,
              ) ?? false,
            onReturn: item.item?.on_return ?? undefined,
          };
        });
      }

      // 2의 배수로 맞추기
      if (twoColItems.length % 2 !== 0) {
        twoColItems.push({
          type: 'placeholder',
          key: `placeholder-${component.componentId}-0`,
        });
      }

      for (let i = 0; i < twoColItems.length; i += 2) {
        const chunk = twoColItems.slice(i, i + 2);
        items.push({
          kind: 'row-2col',
          key: rowKey('row2', rowCounter++, chunk),
          items: chunk,
        });
      }
    } else {
      // === 3col 평탄화 ===
      let rowItems: RowItem[] = [];

      if (itemListType === AtomicItemListType.ThreeColGoodsList) {
        const goodsData = getThreeColGoodsListData(entityList);
        rowItems = goodsData.map((item, ei) => {
          if (item.type === 'placeholder') {
            return { type: 'placeholder' as const, key: item.key };
          }
          return {
            type: 'goods' as const,
            sno: item.data.sno,
            data: item.data,
            componentIndex: rowComponentIndex,
            entityIndex: ei,
            logging: item.data.logging,
            ad: item.data.item?.ad,
            firstPageRendering: item.data.item?.first_page_rendering ?? null,
            listId: item.data.logging?.analytics?.LIST_ID as string | undefined,
            feedbackType:
              (item.data.item?.feedback_type as FeedbackType | null) ?? null,
            hasMoreAction:
              item.data.render?.data.actions?.includes(
                AblyGoodsCardActionType.More,
              ) ?? false,
            onReturn: item.data.item?.on_return ?? undefined,
          };
        });
      } else if (itemListType === AtomicItemListType.ThreeColCardList) {
        if (!entityList) continue;
        const cardData = cardRenderItemGenerator(entityList);
        let goodsIdx = 0;
        rowItems = cardData
          .filter(item => item.type !== 'PRIMARY_CARD')
          .map(item => {
            const ei = goodsIdx++;
            if (
              item.type === 'VIDEO_GOODS_CARD' ||
              item.type === 'FULL_VIDEO_GOODS_CARD'
            ) {
              return {
                type: 'videoGoods' as const,
                cardType: item.type,
                sno: item.item.sno,
                item: item.item,
                logging: item.logging,
                componentIndex: rowComponentIndex,
                entityIndex: ei,
              };
            }
            return {
              type: 'goods' as const,
              sno: item.sno,
              data: item,
              componentIndex: rowComponentIndex,
              entityIndex: ei,
              logging: item.logging,
              ad: item.item?.ad,
              firstPageRendering: item.item?.first_page_rendering ?? null,
              listId: item.logging?.analytics?.LIST_ID as string | undefined,
              feedbackType:
                (item.item?.feedback_type as FeedbackType | null) ?? null,
              hasMoreAction:
                item.render?.data.actions?.includes(
                  AblyGoodsCardActionType.More,
                ) ?? false,
              onReturn: item.item?.on_return ?? undefined,
            };
          });

        // 3의 배수로 맞추기
        const remainder = rowItems.length % 3;
        if (remainder !== 0) {
          for (let i = 0; i < 3 - remainder; i++) {
            rowItems.push({
              type: 'placeholder',
              key: `placeholder-${component.componentId}-${i}`,
            });
          }
        }
      }

      for (let i = 0; i < rowItems.length; i += 3) {
        const chunk = rowItems.slice(i, i + 3);
        items.push({
          kind: 'row-3col',
          key: rowKey('row3', rowCounter++, chunk),
          items: chunk,
        });
      }
    }

    // 평탄화 대상의 푸터 — 별도 행으로 분리 (분기 밖 단일 위치)
    // 새 평탄화 타입 추가 시 푸터 처리 누락을 방지하기 위해 한 곳에서 push
    if (component.type.footer && component.entity.footer) {
      items.push({
        kind: 'footer',
        key: `footer-${ci}-${component.componentId}`,
        component,
        originalIndex: dataIdx,
      });
    }
  }
  return items;
};

export const useFlattenedItems = (
  effectiveComponents: IScreensComponent[],
  originalIndices?: number[],
): FlattenedItem[] => {
  return useMemo(
    () => flattenComponents(effectiveComponents, originalIndices),
    [effectiveComponents, originalIndices],
  );
};
